import { Link } from "@mui/material";

import {
  faqs,
  howItWorks,
  purchaseSeatLanguages,
  TryCityDustDevilsLink,
} from "./helpers";
import { routes } from "@/global/routes";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";
import { vendorUUIDs } from "../../constants";

import StaticSeatMap from "public/images/tricitydustdevils/seatmap.png";
import Background from "public/images/tricitydustdevils/background.jpeg";
import FindGamesBanner from "public/images/tricitydustdevils/banner.png";

const LogoImage =
  "https://ss-leagues.s3.amazonaws.com/Minor%20League%20Baseball/Tri-City_Dust_Devils_logo.png";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "tricitydustdevils_flex",
  browserTitle: "Dust Devils Buy More Save More",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Tri-City Dust Devils",

  dynamicPricing: {
    enabled: true,
    showAllDiscounts: true,
    list: [
      {
        id: 1,
        discount: 15,
        minSelectedAmount: 2,
        updatedAccountGroup: 0,
        customDiscountMessage: "$15 a ticket",
        customProgressBarMessage:
          "Congrats! You've unlocked 2 games at $15 tickets! Add 1 more game for $14 a ticket, 2 more games for $13 a ticket, or 3 more games for $12 a ticket.",
      },
      {
        id: 2,
        discount: 14,
        minSelectedAmount: 3,
        updatedAccountGroup: 1,
        customDiscountMessage: "$14 a ticket",
        customProgressBarMessage:
          "Congrats! You've unlocked 3 games at $14 tickets! Add 1 more game for $13 a ticket or 1 more game for $12 a ticket.",
      },
      {
        id: 3,
        discount: 13,
        minSelectedAmount: 4,
        updatedAccountGroup: 2,
        customDiscountMessage: "$13 a ticket",
        customProgressBarMessage:
          "Congrats! You've unlocked 4 games at $13 tickets! Add 1 more game for $12 a ticket or checkout now.",
      },
      {
        id: 4,
        discount: 12,
        minSelectedAmount: 5,
        updatedAccountGroup: 3,
        customDiscountMessage: "$12 a ticket",
        customProgressBarMessage:
          "Congrats! You've unlocked 5 games for $12 tickets!",
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    mapId: "",
    venueId: "",
    isView3dButtonHidden: true,
    staticSeatMapImage: StaticSeatMap.src,
    isShowAvailableSeatsButtonHidden: true,
    customSeatMapStaticImageStyle: {
      width: "52%",
      marginX: "auto",
      display: "block",
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isPurchaseCreditHistoryEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isStandAlonePurchaseCreditEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: "SEAT-LEVEL",

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 2,
    maxGameSelectionCount: 5,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: true,
    isCheckoutEditSeatsEnabled: true,
    isDiffSeatAlertEnabled: true,
    defaultSeatCount: 2,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl:
    "https://mlb.tickets.com/?agency=MILB_MPV&orgid=57349#/auth/login?fullret=%2Fschedule%2F%3Fagency%3DMILB_MPV%26orgid%3D57349%23%2F%3Fview%3Dlist%26includePackages%3Dtrue0",

  // vendor
  platform: vendorUUIDs.tickesdotcom,

  // okta config
  oktaIssuer: "https://ids.mlb.com/oauth2/aus1m088yK07noBfh356",
  oktaClientId: "0oasmcdgh92Jom7TI357",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: "",
    authBackgroundImage: Background.src,
    checkoutStatusPageImage: Background.src,
    findGamesBannerImage: FindGamesBanner.src,
    logoImage: LogoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      hideOverlayLogo: false,
      showLogoWithTitle: false,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideAccessibilityFilter: false,
      hideSectionFilter: false,
    },

    shouldDisplayServiceFee: false,

    progressBar: {
      staticProgressBarText: false,
    },

    customAdaLabel: (
      <>
        To purchase accessible seating locations, please contact the Box Office
        at{" "}
        <Link
          target="_blank"
          href="tel:7184498497"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (718) 449-8497
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#003061",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    redeemGiftCard: null,
    topNavMenuItems: null,
    purchaseGiftCard: null,
    purchaseSeats: purchaseSeatLanguages,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription: (
      <>
        Your Tri-City Dust Devils tickets are confirmed! Your tickets will be
        available in your <TryCityDustDevilsLink /> Account shortly.
      </>
    ),

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
